import { useState } from "react";
import { Link } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import AuthLayout from "@/components/layout/AuthLayout";
import { usePostForgotPassword } from "@/hooks/useAuth";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { FormInput } from "@/components/forms/FormControls";
import { forgotPasswordSchema, type ForgotPasswordValues } from "@/schemas/auth";
import { PUBLIC_ROUTES } from "@/routes/url";

export default function ForgotPassword() {
  const [sent, setSent] = useState(false);
  const { mutateAsync, isPending: busy } = usePostForgotPassword();
  const form = useForm<ForgotPasswordValues>({ resolver: zodResolver(forgotPasswordSchema), defaultValues: { email: "" } });

  const submit = async ({ email }: ForgotPasswordValues) => {
    try {
      await mutateAsync({ email, origin_url: window.location.origin });
      setSent(true);
    } catch {
      // The mutation hook displays error.message.
    }
  };

  return (
    <AuthLayout>
        {sent ? (
          <div data-testid="forgot-sent">
            <h1 className="font-display text-4xl tracking-tight text-center">Reset link on its way.</h1>
            <p className="text-stone text-sm mt-3 leading-relaxed text-center">
              If an account exists for <span className="text-ink">{form.getValues("email")}</span>, we&apos;ve emailed a link to reset the password.
              The link expires in 30 minutes. Check spam if you don&apos;t see it.
            </p>
            <Link to={PUBLIC_ROUTES.LOGIN} className="mt-8 block text-center text-sm text-moss underline underline-offset-4" data-testid="forgot-back-to-login">
              Back to sign in
            </Link>
          </div>
        ) : (
          <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} data-testid="forgot-form" noValidate>
            <h1 className="font-display text-4xl tracking-tight text-center">Forgot your password?</h1>
            <p className="text-stone text-sm mt-3 leading-relaxed text-center">
              Enter the email you use to sign in. We&apos;ll email you a link to choose a new one.
            </p>
            <div className="mt-8"><FormInput name="email" label="Email" type="email" placeholder="Enter your email" autoComplete="email" data-testid="forgot-email" required /></div>
            <Button
              type="submit"
              variant="dashboardPrimary"
              size="lg"
              loading={busy}
              className="mt-8 w-full"
              data-testid="forgot-submit"
            >
              Send reset link
            </Button>
            <div className="mt-6 text-sm text-stone text-center">
              Remembered it? <Link to={PUBLIC_ROUTES.LOGIN} className="text-moss underline underline-offset-4" data-testid="forgot-signin-link">Sign in</Link>
            </div>
          </form>
          </Form>
        )}
    </AuthLayout>
  );
}
